import { AbsolutePath, Schema, SchemaKt } from "@kform/core";
import * as React from "react";

import { FormContext } from "../contexts/FormContext";
import { FormOptions, useForm } from "../hooks/useForm";

/**
 * Properties of the {@link Form} component.
 */
export type FormProps<T = unknown, TSubmitResult = unknown> = FormOptions<
  T,
  TSubmitResult
> &
  FormOwnProps<T> &
  React.ComponentPropsWithoutRef<"form">;

/**
 * Own properties of the {@link Form} component.
 */
export interface FormOwnProps<T = unknown> {
  /**
   * Schema of the form.
   */
  schema: Schema<T> | SchemaKt;
}

/**
 * Component exposing a form managed by a [form manager]{@link FormManager},
 * initialised with the provided schema.
 */
export const Form = React.forwardRef(function Form<
  T = unknown,
  TSubmitResult = unknown,
>(
  {
    schema,
    initialValue,
    enabled,
    _defaultState,
    onInitialized,
    onUninitialized,
    onFormManagerEvent,
    onValueChange,
    onValidationStatusChange,
    onDisplayStatusChange,
    onDirtyStatusChange,
    onTouchedStatusChange,
    externalContexts,
    validationMode,
    confirmUnloadWhenDirty,
    confirmUnloadMessage,
    onSubmit,
    onInvalidSubmit,
    onSuccessfulSubmit,
    onFailedSubmit,
    setTouchedOnSubmit,
    validateOnSubmit,
    setPristineOnSuccessfulSubmit,
    convertExternalIssuesTableRowIndicesToIds,
    onReset,
    ...otherProps
  }: FormProps<T, TSubmitResult>,
  forwardedRef: React.ForwardedRef<React.ElementRef<"form">>,
) {
  const formController = useForm<T, TSubmitResult>(schema, {
    initialValue,
    enabled,
    _defaultState,
    onInitialized,
    onUninitialized,
    onFormManagerEvent,
    onValueChange,
    onValidationStatusChange,
    onDisplayStatusChange,
    onDirtyStatusChange,
    onTouchedStatusChange,
    externalContexts,
    validationMode,
    confirmUnloadWhenDirty,
    confirmUnloadMessage,
    onSubmit,
    onInvalidSubmit,
    onSuccessfulSubmit,
    onFailedSubmit,
    setTouchedOnSubmit,
    validateOnSubmit,
    setPristineOnSuccessfulSubmit,
    convertExternalIssuesTableRowIndicesToIds,
    onReset,
  });

  const formManager = formController.useFormManager();
  const formContext = React.useMemo(
    () => ({
      formManager,
      currentPath: AbsolutePath.ROOT,
      controller: formController,
    }),
    [formController, formManager],
  );

  return (
    <FormContext.Provider value={formContext}>
      <form {...formController.formProps} {...otherProps} ref={forwardedRef} />
    </FormContext.Provider>
  );
});
